import { makeAutoObservable } from "mobx";
import { DecisionContext, DissolutionStatus } from "@/dbot/common/commonDomain";
import { DecisionType } from "@/common/decisions";
import { BooleanFieldModel } from "@/common/commonForms";
import { TMessage } from "@/i18n/tt";

export type LiquidateCompany = {
  type: DecisionType.liquidateCompany;
  dissolutionGeneralMeetingDate?: string | null;
  isDissolutionResolved?: boolean | null;
  isCreditorNoticeExpired?: boolean | null;
  isFinalSettlementAudited?: boolean | null;
};

function isCreditorPeriodRunning(dissolution: DissolutionStatus | undefined): boolean {
  return Boolean(dissolution?.brRegisteredDate) && !dissolution?.isCreditorNoticeExpired;
}

export class LiquidateCompanyForm {
  readonly type = DecisionType.liquidateCompany;
  isDissolutionResolved: BooleanFieldModel;
  isCreditorNoticeExpired: BooleanFieldModel;
  isFinalSettlementAudited: BooleanFieldModel;
  readonly dissolutionGeneralMeetingDate?: string;

  constructor(
    readonly context: DecisionContext<LiquidateCompany>,
    data?: LiquidateCompany,
  ) {
    const dissolution = context.dissolutionStatus;
    this.dissolutionGeneralMeetingDate =
      data?.dissolutionGeneralMeetingDate ?? dissolution?.generalMeetingDate;
    this.isDissolutionResolved = new BooleanFieldModel({
      value: data?.isDissolutionResolved ?? (dissolution?.generalMeetingDate ? true : undefined),
      requiredTrue: true,
    });
    this.isCreditorNoticeExpired = new BooleanFieldModel({
      value: isCreditorPeriodRunning(dissolution)
        ? false
        : (data?.isCreditorNoticeExpired ?? (dissolution?.isCreditorNoticeExpired || undefined)),
      requiredTrue: true,
    });
    this.isFinalSettlementAudited = new BooleanFieldModel({
      value: data?.isFinalSettlementAudited ?? Boolean(context.initialState.currentAuditor),
      required: true,
    });
    makeAutoObservable(
      this,
      { type: false, context: false, dissolutionGeneralMeetingDate: false },
      { autoBind: true },
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get creditorNoticeExpiryDate(): string | undefined {
    return this.context.dissolutionStatus?.creditorNoticeExpiryDate;
  }

  get brRegisteredDate(): string | undefined {
    return this.context.dissolutionStatus?.brRegisteredDate;
  }

  get auditorName(): string | undefined {
    return this.context.initialState.currentAuditor?.name;
  }

  get isCreditorPeriodRunning(): boolean {
    return isCreditorPeriodRunning(this.context.dissolutionStatus);
  }

  get creditorError(): TMessage | undefined {
    return this.isCreditorPeriodRunning ? undefined : this.isCreditorNoticeExpired.error;
  }

  get isDissolutionFound(): boolean {
    return Boolean(this.context.dissolutionStatus?.generalMeetingDate);
  }

  get isDissolutionRejected(): boolean {
    return this.isDissolutionResolved.value === false;
  }

  get dissolutionError(): TMessage | undefined {
    if (this.isDissolutionRejected) {
      return { key: "decisions.liquidateCompany.isDissolutionResolved.required" };
    }
    if (this.isDissolutionResolved.value === true && !this.isDissolutionFound) {
      return { key: "decisions.liquidateCompany.isDissolutionResolved.notFound" };
    }
    return this.isDissolutionResolved.error;
  }

  get isValid(): boolean {
    return (
      this.isDissolutionResolved.isValid &&
      this.isCreditorNoticeExpired.isValid &&
      this.isFinalSettlementAudited.isValid
    );
  }

  validate(): boolean {
    this.isDissolutionResolved.validate();
    this.isCreditorNoticeExpired.validate();
    this.isFinalSettlementAudited.validate();
    return this.isValid;
  }
}
